import { Box, Modal, Button, TextField, Typography } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface Props {
  open: boolean
  user: CognitoUser | null
  action: string
  onClose: () => void
  onSubmit: (payload: CognitoUserEditForm) => void
  onDelete: (payload: string) => void
  onResend: () => void
}

export default function AdminUserEditModal({ open, user, action, onClose, onSubmit, onDelete, onResend }: Props) {
  const { register, handleSubmit, watch } = useForm<CognitoUserEditForm>({
    defaultValues: {
      email: user?.email,
      userId: user?.ggbUserId ? Number(user.ggbUserId) : undefined,
      phone: user?.phoneNumber,
    },
  })
  const intl = useIntl()

  const formValues = watch()
  const initialUserId = user?.ggbUserId ? Number(user.ggbUserId) : undefined
  const isFormDirty =
    (formValues.email ?? '') !== (user?.email ?? '') ||
    (formValues.phone ?? '') !== (user?.phoneNumber ?? '') ||
    String(formValues.userId ?? '') !== String(initialUserId ?? '')

  const submitForm = (data: CognitoUserEditForm) => {
    onSubmit(data)
  }

  const handleDelete = () => {
    if (!user) {
      return
    }
    onDelete(user.username)
  }

  return (
    <>
      {action === 'edit' && (
        <Modal open={open} onClose={onClose}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 500,
                bgcolor: '#FFF',
                borderRadius: '12px',
                boxShadow: 24,
                display: 'flex',
                flexDirection: 'column',
                outline: 'none',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  padding: '20px 22px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  borderBottom: '1px solid #E4E4E7',
                  color: '#022314',
                  fontWeight: '500',
                  lineHeight: '24px',
                }}
              >
                Edit User
                <Button
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: '#8A8A8E',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    flexShrink: 0,
                    minWidth: 0,
                    background: '#FFF',
                    border: 'solid 1px',
                    borderColor: '#E4E4E7',
                  }}
                  onClick={onClose}
                >
                  <ClearIcon sx={{ fontSize: '18px', fontWeight: 400, lineHeight: 'normal' }} />
                </Button>
              </Box>

              {/* Body */}
              <Box
                sx={{
                  display: 'flex',
                  padding: '20px 22px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '17px',
                  alignSelf: 'stretch',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                  <Typography
                    sx={{
                      color: '#022314',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    }}
                  >
                    Email*
                  </Typography>
                  <TextField
                    type="email"
                    placeholder="name@eggsample.com"
                    required
                    {...register('email')}
                    sx={{
                      width: '100%',
                      padding: '10px, 12px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        height: '40px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '& .MuiInputBase-root': {
                          width: '100%',
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                  <Typography
                    sx={{
                      color: '#022314',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    }}
                  >
                    Phone Number
                  </Typography>
                  <TextField
                    type="tel"
                    placeholder="+385..."
                    {...register('phone')}
                    sx={{
                      width: '100%',
                      padding: '10px, 12px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        height: '40px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '& .MuiInputBase-root': {
                          width: '100%',
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                  <Typography
                    sx={{
                      color: '#022314',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20px',
                    }}
                  >
                    User ID
                  </Typography>
                  <TextField
                    type="number"
                    placeholder="Golf GameBook user id"
                    {...register('userId')}
                    sx={{
                      width: '100%',
                      padding: '10px, 12px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        height: '40px',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        '& .MuiInputBase-root': {
                          width: '100%',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
              {/* Footer */}

              <Box
                sx={{
                  display: 'flex',
                  padding: '16px 20px',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    minWidth: 'auto',
                    borderRadius: '10px',
                    border: '1px solid #E4E4E7',
                    background: '#FAFAFA',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                    display: 'flex',
                    height: '36px',
                    padding: '8px 12px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#022314',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  disabled={!isFormDirty}
                  sx={{
                    minWidth: 'auto',
                    borderRadius: '10px',
                    background: '#0BB163',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                    display: 'flex',
                    height: '36px',
                    padding: '8px 12px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      background: '#099856',
                    },
                    '&.Mui-disabled': {
                      color: '#F7FEE7',
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </form>
        </Modal>
      )}
      {action === 'resendInvitation' && (
        <Modal open={open} onClose={onClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: '#FFF',
              borderRadius: '12px',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
                borderBottom: '1px solid #E4E4E7',
                color: '#022314',
                fontWeight: '500',
                lineHeight: '24px',
              }}
            >
              Resend Invitation
              <Button
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  color: '#8A8A8E',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  flexShrink: 0,
                  minWidth: 0,
                  background: '#FFF',
                  border: 'solid 1px',
                  borderColor: '#E4E4E7',
                }}
                onClick={onClose}
              >
                <ClearIcon sx={{ fontSize: '18px', fontWeight: 400, lineHeight: 'normal' }} />
              </Button>
            </Box>

            {/* Body */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '17px',
                alignSelf: 'stretch',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                  }}
                >
                  {intl.formatMessage({ id: 'strings.adminUserResendInvitationMessage' }) + ' to ' + user?.email}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                padding: '16px 20px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch',
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  border: '1px solid #E4E4E7',
                  background: '#FAFAFA',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#022314',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  background: '#0BB163',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    background: '#099856',
                  },
                }}
                onClick={onResend}
              >
                Resend
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      {action === 'delete' && (
        <Modal open={open} onClose={onClose}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: '#FFF',
              borderRadius: '12px',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
                borderBottom: '1px solid #E4E4E7',
                color: '#022314',
                fontWeight: '500',
                lineHeight: '24px',
              }}
            >
              Delete
              <Button
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  color: '#8A8A8E',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  flexShrink: 0,
                  minWidth: 0,
                  background: '#FFF',
                  border: 'solid 1px',
                  borderColor: '#E4E4E7',
                }}
                onClick={onClose}
              >
                <ClearIcon sx={{ fontSize: '18px', fontWeight: 400, lineHeight: 'normal' }} />
              </Button>
            </Box>

            {/* Body */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '17px',
                alignSelf: 'stretch',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                  }}
                >
                  {intl.formatMessage({ id: 'strings.cognitoDeleteUserMessage' })}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                padding: '16px 20px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch',
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  border: '1px solid #E4E4E7',
                  background: '#FAFAFA',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#022314',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  background: '#E7000B',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    background: '#C40009',
                  },
                }}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  )
}
